// src/pages/patient/DoctorChat.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { doctorCareApi, doctorChatSocketUrl } from "../../services/doctorCare";

function parseApiError(err, fallback = "Something went wrong. Please try again.") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || item?.message || JSON.stringify(item)).join(", ");
  }
  return err?.response?.data?.message || err?.message || fallback;
}

function classNames(...items) {
  return items.filter(Boolean).join(" ");
}

function shortId(value) {
  if (!value) return "";
  return String(value).split("-")[0]?.toUpperCase() || String(value).slice(0, 8).toUpperCase();
}

function displayTitle(item) {
  const title = String(item?.title || "").trim();
  if (title && title !== "New conversation") return title;
  const preview = String(item?.last_message_preview || "").trim();
  if (preview) return preview.length > 70 ? `${preview.slice(0, 67)}...` : preview;
  return "New conversation";
}

function Icon({ name, className = "h-5 w-5" }) {
  const common = { className, viewBox: "0 0 24 24", fill: "none" };

  if (name === "send") {
    return (
      <svg {...common}>
        <path d="M22 2 11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="m22 2-7 20-4-9-9-4 20-7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    );
  }

  if (name === "clip") {
    return (
      <svg {...common}>
        <path d="m21.4 11.6-8.5 8.5a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 1 1-2.8-2.8l8.5-8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (name === "download") {
    return (
      <svg {...common}>
        <path d="M12 3v12m0 0 4-4m-4 4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (name === "shield") {
    return (
      <svg {...common}>
        <path d="M12 3 5 6v6c0 4.5 3 7.4 7 9 4-1.6 7-4.5 7-9V6l-7-3Z" stroke="currentColor" strokeWidth="2" />
        <path d="m9 12 2 2 4-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (name === "wifi") {
    return (
      <svg {...common}>
        <path d="M5 13a10 10 0 0 1 14 0M8.5 16.5a5 5 0 0 1 7 0M12 20h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (name === "plus") {
    return (
      <svg {...common}>
        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      </svg>
    );
  }

  if (name === "message") {
    return (
      <svg {...common}>
        <path d="M21 12a8 8 0 0 1-8 8H8l-5 3 1.7-5A8 8 0 1 1 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    );
  }

  if (name === "sparkle") {
    return (
      <svg {...common}>
        <path d="M12 3 9.8 8.8 4 11l5.8 2.2L12 19l2.2-5.8L20 11l-5.8-2.2L12 3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M19 3v4M21 5h-4M5 17v3M6.5 18.5h-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (name === "x") {
    return (
      <svg {...common}>
        <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      </svg>
    );
  }

  if (name === "back") {
    return (
      <svg {...common}>
        <path d="M15 18 9 12l6-6" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return null;
}

function formatTime(value) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function fileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function initials(name) {
  return String(name || "DR")
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "DR";
}

function Attachment({ file, mine }) {
  return (
    <a
      href={file.download_url || "#"}
      target="_blank"
      rel="noreferrer"
      onClick={(event) => !file.download_url && event.preventDefault()}
      className={classNames(
        "mt-3 flex items-center gap-3 rounded-2xl border p-3 text-left transition hover:-translate-y-0.5",
        mine ? "border-white/25 bg-white/15 text-white" : "border-slate-200 bg-slate-50 text-slate-800"
      )}
    >
      <div className={classNames("flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl", mine ? "bg-white/20" : "bg-white")}>
        <Icon name="download" className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-bold">{file.file_name || "Attachment"}</div>
        <div className={classNames("mt-0.5 truncate text-xs", mine ? "text-white/75" : "text-slate-500")}>{file.content_type || "File"} {fileSize(file.size_bytes) ? `· ${fileSize(file.size_bytes)}` : ""}</div>
      </div>
    </a>
  );
}

function MessageBubble({ message }) {
  const mine = message.author_type === "patient";

  return (
    <div className={classNames("group flex gap-3", mine ? "justify-end" : "justify-start")}>
      {!mine ? (
        <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white text-xs font-black text-blue-700 shadow-sm ring-1 ring-blue-100">
          DR
        </div>
      ) : null}

      <div className={classNames("max-w-[82%] lg:max-w-[68%]", mine ? "items-end" : "items-start")}>
        <div
          className={classNames(
            "rounded-[26px] px-4 py-3 shadow-sm",
            mine
              ? "rounded-br-lg bg-gradient-to-br from-blue-600 via-sky-500 to-cyan-500 text-white shadow-blue-200/70"
              : "rounded-bl-lg border border-slate-200 bg-white text-slate-850"
          )}
        >
          {message.message ? <div className="whitespace-pre-wrap break-words text-sm leading-6">{message.message}</div> : null}
          {(message.attachments || []).map((attachment) => (
            <Attachment key={attachment.id || attachment.file_key || attachment.file_name} file={attachment} mine={mine} />
          ))}
        </div>
        <div className={classNames("mt-1 px-2 text-[11px] font-semibold", mine ? "text-right text-slate-400" : "text-left text-slate-400")}>
          {mine ? "You" : "Doctor"} · {formatTime(message.created_at)}
        </div>
      </div>
    </div>
  );
}

function ConversationItem({ item, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "w-full rounded-3xl border p-4 text-left transition",
        active
          ? "border-blue-200 bg-blue-50 shadow-[0_14px_35px_rgba(37,99,235,0.12)]"
          : "border-slate-200 bg-white hover:border-blue-100 hover:bg-slate-50"
      )}
    >
      <div className="flex items-start gap-3">
        <div className={classNames("flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl", active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600")}>
          <Icon name="message" className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-black text-slate-950">{displayTitle(item)}</div>
          {/* <div className="mt-1 text-[11px] font-bold uppercase tracking-wide text-blue-500">ID {shortId(item.id)}</div> */}
          <div className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">{item.last_message_preview || "Send the first message to name this conversation."}</div>
          {/* <div className="mt-2 flex items-center justify-between gap-2 text-[11px] font-bold text-slate-400">
            <span>{item.messages_count || 0} messages</span>
            <span>{formatTime(item.last_message_at || item.created_at)}</span>
          </div> */}
        </div>
      </div>
    </button>
  );
}

export default function DoctorChat() {
  const { doctorId } = useParams();
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const activeThreadRef = useRef(null);
  const socketThreadRef = useRef(null);
  const requestSeqRef = useRef(0);

  const [threads, setThreads] = useState([]);
  const [thread, setThread] = useState(null);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState("");

  const messages = useMemo(() => thread?.messages || [], [thread]);
  const doctorName = thread?.doctor_name || threads[0]?.doctor_name || "Doctor";
  const currentTitle = displayTitle(thread);

  function previewFromMessage(value) {
    const text = String(value || "").trim().replace(/\s+/g, " ");
    if (!text) return "Attachment";
    return text.length > 180 ? `${text.slice(0, 177)}...` : text;
  }

  function applyOptimisticMessage(targetThreadId, optimisticMessage, nextPreview) {
    const now = optimisticMessage.created_at;

    setThread((prev) => {
      if (!prev || String(prev.id) !== String(targetThreadId)) return prev;

      const existingMessages = Array.isArray(prev.messages) ? prev.messages : [];
      const existingTitle = String(prev.title || "").trim();
      const shouldRename = !existingTitle || existingTitle === "New conversation" || existingTitle === "General conversation";

      return {
        ...prev,
        title: shouldRename ? nextPreview : prev.title,
        last_message_preview: nextPreview,
        last_message_at: now,
        messages_count: (prev.messages_count || existingMessages.length || 0) + 1,
        messages: [...existingMessages, optimisticMessage],
      };
    });

    setThreads((prev) => {
      let touched = false;
      const next = prev.map((item) => {
        if (String(item.id) !== String(targetThreadId)) return item;
        touched = true;

        const existingTitle = String(item.title || "").trim();
        const shouldRename = !existingTitle || existingTitle === "New conversation" || existingTitle === "General conversation";

        return {
          ...item,
          title: shouldRename ? nextPreview : item.title,
          last_message_preview: nextPreview,
          last_message_at: now,
          messages_count: (item.messages_count || 0) + 1,
        };
      });

      if (!touched) return prev;

      const activeItem = next.find((item) => String(item.id) === String(targetThreadId));
      const rest = next.filter((item) => String(item.id) !== String(targetThreadId));
      return activeItem ? [activeItem, ...rest] : next;
    });
  }

  function removeOptimisticMessage(targetThreadId, optimisticId) {
    setThread((prev) => {
      if (!prev || String(prev.id) !== String(targetThreadId)) return prev;
      return {
        ...prev,
        messages: (prev.messages || []).filter((item) => item.id !== optimisticId),
      };
    });
  }

  function scrollToEnd(behavior = "smooth") {
    window.setTimeout(() => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior });
    }, 60);
  }

  function setActiveThread(threadId) {
    activeThreadRef.current = threadId || null;
    setActiveThreadId(threadId || null);
  }

  async function refreshThreads({ silent = true, refreshCurrent = false } = {}) {
    if (!doctorId) return null;
    if (!silent) setLoading(true);
    try {
      const response = await doctorCareApi.chatThreads(String(doctorId));
      const items = response?.items || [];
      setThreads(items);

      const currentId = activeThreadRef.current;
      if (refreshCurrent && currentId && items.some((item) => item.id === currentId)) {
        const detail = await doctorCareApi.chatThread(String(doctorId), currentId);
        if (activeThreadRef.current === currentId) {
          setThread(detail);
        }
      }
      return items;
    } catch (err) {
      if (!silent) setError(parseApiError(err, "Could not refresh conversations."));
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }

  async function bootChat() {
    if (!doctorId) return;
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError("");
    try {
      const response = await doctorCareApi.chatThreads(String(doctorId));
      if (seq !== requestSeqRef.current) return;

      let items = response?.items || [];
      let selectedId = activeThreadRef.current || items[0]?.id || null;
      let detail = null;

      if (!selectedId) {
        detail = await doctorCareApi.createChatThread(String(doctorId), {});
        if (seq !== requestSeqRef.current) return;
        selectedId = detail.id;
        items = [detail];
      } else {
        detail = await doctorCareApi.chatThread(String(doctorId), selectedId);
        if (seq !== requestSeqRef.current) return;
      }

      setThreads(items);
      setThread(detail);
      setActiveThread(detail.id);
      connectSocket(detail.id);
      scrollToEnd("auto");
    } catch (err) {
      if (seq === requestSeqRef.current) setError(parseApiError(err, "Could not load conversations."));
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }

  async function selectThread(threadId) {
    if (!threadId || threadId === activeThreadRef.current) return;
    const seq = ++requestSeqRef.current;
    setActiveThread(threadId);
    setSwitching(true);
    setError("");
    setFiles([]);
    setMessage("");

    try {
      const detail = await doctorCareApi.chatThread(String(doctorId), threadId);
      if (seq !== requestSeqRef.current || activeThreadRef.current !== threadId) return;
      setThread(detail);
      connectSocket(threadId);
      scrollToEnd("auto");
    } catch (err) {
      if (seq === requestSeqRef.current) setError(parseApiError(err, "Could not open conversation."));
    } finally {
      if (seq === requestSeqRef.current) setSwitching(false);
    }
  }

  function connectSocket(threadId = activeThreadRef.current) {
    if (!doctorId || !threadId) return;

    try {
      const url = doctorChatSocketUrl(String(doctorId), String(threadId));
      if (!url) {
        setConnected(false);
        return;
      }

      wsRef.current?.close?.();
      socketThreadRef.current = String(threadId);
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (socketThreadRef.current === String(threadId)) setConnected(true);
      };
      ws.onerror = () => {
        if (socketThreadRef.current === String(threadId)) setConnected(false);
      };
      ws.onclose = () => {
        if (socketThreadRef.current === String(threadId)) setConnected(false);
      };
      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.type !== "thread" || !payload.thread) return;

          const incomingId = String(payload.thread.id || "");
          const currentId = String(activeThreadRef.current || "");
          const socketId = String(socketThreadRef.current || "");
          if (!incomingId || incomingId !== currentId || incomingId !== socketId) return;

          setThread(payload.thread);
          refreshThreads({ silent: true, refreshCurrent: false });
          scrollToEnd();
        } catch {
          // Ignore malformed websocket events.
        }
      };
    } catch {
      setConnected(false);
    }
  }

  useEffect(() => {
    bootChat();

    const poll = window.setInterval(() => {
      refreshThreads({ silent: true, refreshCurrent: true });
    }, 15000);
    const ping = window.setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 25000);

    return () => {
      requestSeqRef.current += 1;
      window.clearInterval(poll);
      window.clearInterval(ping);
      wsRef.current?.close?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId]);

  function chooseFiles(event) {
    const picked = Array.from(event.target.files || []);
    if (picked.length) setFiles((prev) => [...prev, ...picked]);
    event.target.value = "";
  }

  async function createThread() {
    if (!doctorId || creating) return;
    const seq = ++requestSeqRef.current;
    setCreating(true);
    setError("");
    setFiles([]);
    setMessage("");

    try {
      const detail = await doctorCareApi.createChatThread(String(doctorId), {});
      if (seq !== requestSeqRef.current) return;
      setThread(detail);
      setActiveThread(detail.id);
      setThreads((prev) => [detail, ...prev.filter((item) => item.id !== detail.id)]);
      connectSocket(detail.id);
      scrollToEnd("auto");
    } catch (err) {
      if (seq === requestSeqRef.current) setError(parseApiError(err, "Could not start a new conversation."));
    } finally {
      if (seq === requestSeqRef.current) setCreating(false);
    }
  }

  async function send() {
    const targetThreadId = activeThreadRef.current;
    const text = message.trim();
    const selectedFiles = [...files];

    if (!doctorId || !targetThreadId || (!text && selectedFiles.length === 0) || sending) return;

    const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const now = new Date().toISOString();
    const optimisticAttachments = selectedFiles.map((file, index) => ({
      id: `${optimisticId}-file-${index}`,
      file_name: file.name,
      content_type: file.type || "File",
      size_bytes: file.size,
    }));
    const optimisticMessage = {
      id: optimisticId,
      author_type: "patient",
      message: text || null,
      attachments: optimisticAttachments,
      created_at: now,
      pending: true,
    };
    const nextPreview = previewFromMessage(text || selectedFiles[0]?.name || "Attachment");

    setMessage("");
    setFiles([]);
    setSending(true);
    setError("");
    applyOptimisticMessage(targetThreadId, optimisticMessage, nextPreview);
    scrollToEnd("auto");

    try {
      const attachments = [];
      for (const file of selectedFiles) {
        attachments.push(await doctorCareApi.uploadAttachment(String(doctorId), file));
      }

      if (attachments.length === 0 && wsRef.current?.readyState === WebSocket.OPEN && socketThreadRef.current === String(targetThreadId)) {
        wsRef.current.send(JSON.stringify({ type: "message", message: text }));
        window.setTimeout(() => {
          if (activeThreadRef.current === targetThreadId) {
            refreshThreads({ silent: true, refreshCurrent: true });
          }
        }, 900);
      } else {
        const updated = await doctorCareApi.sendMessage(
          String(doctorId),
          { message: text || null, attachments },
          targetThreadId
        );
        if (activeThreadRef.current === targetThreadId) {
          setThread(updated);
          await refreshThreads({ silent: true, refreshCurrent: false });
        }
      }

      scrollToEnd();
    } catch (err) {
      removeOptimisticMessage(targetThreadId, optimisticId);
      setMessage(text);
      setFiles(selectedFiles);
      setError(parseApiError(err, "Could not send message."));
    } finally {
      setSending(false);
    }
  }

  const pageLoading = loading && !thread;

  return (
    <div className="h-[calc(100vh-9.75rem)] overflow-hidden bg-slate-50 px-3 pb-24 pt-20 lg:h-[calc(100vh-10rem)] lg:px-0 lg:pt-0 lg:pb-0">
      <div className="mx-auto grid h-full max-w-7xl min-h-0 overflow-hidden rounded-[34px] border border-slate-200 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.10)] lg:grid-cols-[360px_minmax(0,1fr)]">
        <aside className="hidden min-h-0 flex-col border-r border-slate-200 bg-white lg:flex">
          <div className="relative shrink-0 overflow-hidden border-b border-slate-200 p-5">
            <div className="absolute -right-12 -top-16 h-40 w-40 rounded-full bg-blue-100 blur-2xl" />
            <div className="absolute -bottom-12 left-12 h-32 w-32 rounded-full bg-cyan-100 blur-2xl" />

            <Link to={`/patient/doctors/${doctorId}`} className="relative inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-black text-slate-700 hover:bg-slate-50">
              <Icon name="back" className="h-4 w-4" />
              Doctor profile
            </Link>

            <div className="relative mt-5 flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-[26px] bg-gradient-to-br from-blue-600 via-sky-500 to-cyan-500 text-lg font-black text-white shadow-lg shadow-blue-200">
                {initials(doctorName)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-xl font-black text-slate-950">{doctorName}</div>
                <div className="mt-1 flex items-center gap-2 text-xs font-bold text-slate-500">
                  <span className={classNames("h-2 w-2 rounded-full", connected ? "bg-emerald-500" : "bg-slate-300")} />
                  {connected ? "Live chat connected" : "Secure fallback active"}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={createThread}
              disabled={creating}
              className="relative mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-500 px-4 py-3 text-sm font-black text-white shadow-lg shadow-blue-100 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
            >
              <Icon name="plus" className="h-5 w-5" />
              {creating ? "Starting..." : "New conversation"}
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <div className="mb-3 flex items-center justify-between px-1">
              <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">Conversations</div>
              <div className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-black text-slate-500">{threads.length}</div>
            </div>

            <div className="space-y-3">
              {threads.length === 0 && !pageLoading ? (
                <div className="rounded-3xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">No conversations yet.</div>
              ) : null}

              {threads.map((item) => (
                <ConversationItem key={item.id} item={item} active={item.id === activeThreadId} onClick={() => selectThread(item.id)} />
              ))}
            </div>
          </div>

          <div className="shrink-0 border-t border-slate-200 p-4">
            <div className="rounded-3xl border border-emerald-100 bg-emerald-50 p-4 text-sm leading-6 text-emerald-800">
              <div className="flex gap-2 font-black">
                <Icon name="shield" className="h-5 w-5 shrink-0" />
                Secure care chat
              </div>
              <p className="mt-1 text-xs leading-5 text-emerald-700">Each conversation has its own ID and is named automatically from the first message.</p>
            </div>
          </div>
        </aside>

        <main className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-slate-50">
          <header className="relative shrink-0 overflow-hidden border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
            <div className="absolute right-8 top-[-70px] h-36 w-36 rounded-full bg-blue-100 blur-3xl" />
            <div className="relative flex items-center gap-3">
              <Link to={`/patient/doctors/${doctorId}`} className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 lg:hidden">
                <Icon name="back" className="h-5 w-5" />
              </Link>

              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 text-sm font-black text-white shadow-lg shadow-blue-100">
                {initials(doctorName)}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <h1 className="min-w-0 truncate text-lg font-black text-slate-950">{currentTitle}</h1>
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-[11px] font-black text-blue-700">
                    <Icon name="sparkle" className="h-3.5 w-3.5" />
                    ID {shortId(activeThreadId)}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs font-bold text-slate-500">
                  <Icon name="wifi" className={classNames("h-4 w-4", connected ? "text-emerald-600" : "text-slate-400")} />
                  {connected ? "Live secure chat with" : "Secure chat with"} {doctorName}
                </div>
              </div>

              <button type="button" onClick={() => refreshThreads({ silent: false, refreshCurrent: true })} className="hidden rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-50 sm:inline-flex">
                Refresh
              </button>
            </div>
          </header>

          <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button type="button" onClick={createThread} disabled={creating} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-blue-600 px-4 py-2.5 text-sm font-black text-white disabled:opacity-60">
                <Icon name="plus" className="h-4 w-4" />
                {creating ? "Starting" : "New"}
              </button>
              {threads.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => selectThread(item.id)}
                  className={classNames(
                    "max-w-[240px] shrink-0 truncate rounded-2xl border px-4 py-2.5 text-sm font-black",
                    item.id === activeThreadId ? "border-blue-200 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600"
                  )}
                  title={item.id}
                >
                  {displayTitle(item)} · {shortId(item.id)}
                </button>
              ))}
            </div>
          </div>

          {error ? <div className="mx-4 mt-4 shrink-0 rounded-3xl border border-rose-200 bg-rose-50 p-4 text-sm font-semibold text-rose-700 sm:mx-6">{error}</div> : null}

          <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.08),transparent_34%),linear-gradient(180deg,#f8fafc,#eef6ff)] p-4 sm:p-6">
            {pageLoading || switching ? (
              <div className="flex h-full items-center justify-center">
                <div className="rounded-3xl border border-slate-200 bg-white px-6 py-5 text-center shadow-sm">
                  <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
                  <div className="mt-3 text-sm font-bold text-slate-500">{switching ? "Opening conversation..." : "Loading conversation..."}</div>
                </div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <div className="max-w-md rounded-[32px] border border-slate-200 bg-white p-8 text-center shadow-sm">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[26px] bg-blue-50 text-blue-700">
                    <Icon name="message" className="h-8 w-8" />
                  </div>
                  <h2 className="mt-5 text-xl font-black text-slate-950">Start this conversation</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-500">Send the first message and we will use it as this conversation&apos;s name automatically.</p>
                  {activeThreadId ? <div className="mt-4 rounded-2xl bg-slate-50 px-4 py-2 text-xs font-black text-slate-500">Conversation ID: {shortId(activeThreadId)}</div> : null}
                </div>
              </div>
            ) : (
              <div className="space-y-5 pb-2">
                {messages.map((item) => (
                  <MessageBubble key={item.id} message={item} />
                ))}
              </div>
            )}
          </div>

          <footer className="shrink-0 border-t border-slate-200 bg-white p-3 sm:p-4">
            {files.length > 0 ? (
              <div className="mb-3 flex max-h-20 flex-wrap gap-2 overflow-y-auto">
                {files.map((file, index) => (
                  <span key={`${file.name}-${index}`} className="inline-flex max-w-xs items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-bold text-blue-700">
                    <span className="truncate">{file.name}</span>
                    <button type="button" onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))} className="rounded-full bg-white p-1 text-blue-700">
                      <Icon name="x" className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}

            <div className="flex items-end gap-2 rounded-[28px] border border-slate-200 bg-slate-50 p-2 shadow-inner sm:gap-3">
              <input ref={fileInputRef} type="file" multiple className="hidden" onChange={chooseFiles} />
              <button type="button" onClick={() => fileInputRef.current?.click()} className="mb-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-600 shadow-sm transition hover:text-blue-700 sm:h-12 sm:w-12" disabled={sending}>
                <Icon name="clip" className="h-5 w-5" />
              </button>
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    send();
                  }
                }}
                rows={1}
                className="max-h-24 min-h-[44px] min-w-0 flex-1 resize-none bg-transparent px-1 py-2.5 text-sm leading-6 text-slate-900 outline-none placeholder:text-slate-400 sm:max-h-32 sm:min-h-[48px] sm:py-3"
                placeholder="Write a message. Shift + Enter for new line."
                disabled={sending || !activeThreadId || switching}
              />
              <button
                type="button"
                onClick={send}
                disabled={sending || switching || !activeThreadId || (!message.trim() && files.length === 0)}
                className="mb-0.5 flex h-11 shrink-0 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-500 px-4 text-sm font-black text-white shadow-lg shadow-blue-100 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 sm:h-12 sm:px-5"
              >
                <span className="hidden sm:inline">{sending ? "Sending" : "Send"}</span>
                <Icon name="send" className="h-5 w-5" />
              </button>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}
